export interface DateRange {
  start: Date;
  end: Date;
}

export type Week = (Date | null)[];
export const DAYS_OF_WEEK: number[] = [0, 1, 2, 3, 4, 5, 6];

const DAYS_PER_WEEK = 7;
const WEEKS_PER_MONTH = 6;

export const addDays = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() + days);
  return newDate;
};

export const addDay = (date: Date): Date => addDays(date, 1);

export const getWeeks = (month: number, year: number): Week[] => {
  const firstOfMonth = new Date(year, month, 1);
  const weekMatrix: Week[][] = new Array<Week[]>(WEEKS_PER_MONTH).fill(
    new Array<Week[number]>(DAYS_PER_WEEK).fill(null)
  );

  let currentDate = firstOfMonth;

  const weeks = weekMatrix.map((week, weekIndex) =>
    week.map((_, dayIndex) => {
      const index = dayIndex + weekIndex * DAYS_PER_WEEK;

      if (index < firstOfMonth.getDay()) {
        const offsetDays = (firstOfMonth.getDay() - index) * -1;
        return addDays(firstOfMonth, offsetDays);
      }
      if (index === firstOfMonth.getDay()) {
        return firstOfMonth;
      }

      currentDate = addDay(currentDate);
      return currentDate;
    })
  );

  // Some months have 6 weeks, some have 5. If the last week is not part of the current month, remove it.
  const firstDayOfLastWeek = weeks[weeks.length - 1][0];
  if (firstDayOfLastWeek.getMonth() !== month) {
    weeks.pop();
  }

  return weeks;
};

export const getPreviousMonth = (month: number): number => {
  if (month === 0) {
    return 11;
  }
  return month - 1;
};

export const getNextMonth = (month: number): number => {
  if (month === 11) {
    return 0;
  }
  return month + 1;
};

export const getPreviousDisplayYear = (month: number, year: number): number => {
  if (month === 0) {
    return year - 1;
  }
  return year;
};

export const getNextDisplayYear = (month: number, year: number): number => {
  if (month === 11) {
    return year + 1;
  }
  return year;
};

export const isSameDate = (date1: Date, date2: Date): boolean =>
  date1.getDate() === date2.getDate() &&
  date1.getMonth() === date2.getMonth() &&
  date1.getFullYear() === date2.getFullYear();

export const isDateBefore = (date: Date, comparisonDate: Date): boolean =>
  date.valueOf() < comparisonDate.valueOf();

export const isDateAfter = (date: Date, comparisonDate: Date): boolean =>
  date.valueOf() > comparisonDate.valueOf();

export const isDateInRange = (date: Date | undefined, range: DateRange): boolean => {
  if (!date) {
    return false;
  }

  const { start, end } = range;

  return start && date > start && end && date < end;
};

export const isDateStart = (date: Date | undefined, selected: DateRange): boolean => {
  if (!date) {
    return false;
  }

  const { start } = selected;

  return start && isSameDate(start, date);
};

export const isDateEnd = (date: Date | undefined, selected: DateRange): boolean => {
  if (!date) {
    return false;
  }

  const { end } = selected;

  return end && isSameDate(end, date);
};

export const isDateSelected = (date: Date | undefined, selected: DateRange): boolean => {
  if (!date) {
    return false;
  }

  const { start, end } = selected;

  return (start && isSameDate(start, date)) || (end && isSameDate(end, date));
};

export const getMonthName = (month: number): string => {
  switch (month) {
    case 0:
      return 'January';
    case 1:
      return 'February';
    case 2:
      return 'March';
    case 3:
      return 'April';
    case 4:
      return 'May';
    case 5:
      return 'June';
    case 6:
      return 'July';
    case 7:
      return 'August';
    case 8:
      return 'September';
    case 9:
      return 'October';
    case 10:
      return 'November';
    case 11:
      return 'December';
    default:
      return '';
  }
};

export const getDayName = (day: number): string => {
  switch (day) {
    case 0:
      return 'Su';
    case 1:
      return 'M';
    case 2:
      return 'Tu';
    case 3:
      return 'W';
    case 4:
      return 'Th';
    case 5:
      return 'F';
    case 6:
      return 'Sa';
    default:
      return '';
  }
};

export const deriveDateRange = (selected?: Date | DateRange): DateRange => {
  if (selected === undefined) {
    return { start: new Date(), end: new Date() };
  }
  return selected instanceof Date ? { start: selected, end: selected } : selected;
};

export const createNewDateRange = (range: DateRange | undefined, selected: Date): DateRange => {
  if (!range) {
    return { start: selected, end: selected };
  }

  const { start, end } = range;

  if (end && (isDateAfter(start, end) || isDateBefore(start, end))) {
    return { start: selected, end: selected };
  }

  if (start) {
    if (isDateBefore(selected, start)) {
      return { start: selected, end: selected };
    }
    return { start, end: selected };
  }

  if (end) {
    if (isDateBefore(selected, end)) {
      return { start: selected, end };
    }
    return { start: start || end, end: selected };
  }

  return { start: selected, end: selected };
};

export const getWeekStartDate = (date: Date, weekStartDay = 0): Date => {
  const currentDay = date.getDay();
  const daysFromStart = (currentDay - weekStartDay + 7) % 7;
  return addDays(date, -daysFromStart);
};

export const getWeekEndDate = (date: Date, weekStartDay = 0): Date => {
  const startDate = getWeekStartDate(date, weekStartDay);
  return addDays(startDate, 6);
};

export const getWeekDates = (referenceDate: Date, weekStartDay = 0): Date[] => {
  const startDate = getWeekStartDate(referenceDate, weekStartDay);
  return Array.from({ length: 7 }, (_, index) => addDays(startDate, index));
};

export const getPreviousWeek = (currentDate: Date): Date => addDays(currentDate, -7);

export const getNextWeek = (currentDate: Date): Date => addDays(currentDate, 7);

export const getAbbreviatedMonthName = (month: number): string => {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];
  return monthNames[month] || '';
};

export const getWeekTitle = (startDate: Date, endDate: Date, showYear = false): string => {
  const startMonth = getAbbreviatedMonthName(startDate.getMonth());
  const endMonth = getAbbreviatedMonthName(endDate.getMonth());
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  if (startDate.getTime() === endDate.getTime()) {
    if (showYear) {
      return `${startMonth} ${startDay}, ${startYear}`;
    }
    return `${startMonth} ${startDay}`;
  }

  if (startDate.getMonth() === endDate.getMonth() && startYear === endYear) {
    if (showYear) {
      return `${startMonth} ${startDay}-${endDay}, ${startYear}`;
    }
    return `${startMonth} ${startDay}-${endDay}`;
  }

  if (startYear !== endYear) {
    if (showYear) {
      return `${startMonth} ${startDay}, ${startYear}-${endMonth} ${endDay}, ${endYear}`;
    }
    return `${startMonth} ${startDay}-${endMonth} ${endDay}`;
  }

  if (showYear) {
    return `${startMonth} ${startDay}-${endMonth} ${endDay}, ${startYear}`;
  }
  return `${startMonth} ${startDay}-${endMonth} ${endDay}`;
};

export type ShowYearMode = 'auto' | 'always' | 'never';

export const shouldShowYear = (
  mode: ShowYearMode | undefined,
  dateToCheck: Date,
  referenceDate: Date = new Date(),
  endDate?: Date
): boolean => {
  if (mode === undefined) {
    mode = 'auto';
  }

  switch (mode) {
    case 'always':
      return true;
    case 'never':
      return false;
    case 'auto':
    default:
      if (endDate && dateToCheck.getFullYear() !== endDate.getFullYear()) {
        return true;
      }
      return dateToCheck.getFullYear() !== referenceDate.getFullYear();
  }
};
